"use client"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Thumbnail from "../thumbnail"
import { resolveProductHref } from "@lib/util/product-href"
import { BiTransfer } from "react-icons/bi"
import {
  formatCurrency,
  formatDiscountedCurrency,
} from "../../../../lib/util/formatCurrency"
import { useState, useCallback, useMemo, useRef, useEffect } from "react"
import { addToCart } from "@lib/data/cart"
import { useCart } from "@context/cart-context"
import { useAnalytics } from "@context/analytics-context"
import { mapProductToItem } from "@lib/analytics/mappers"
import QuickAddDrawer, {
  extractVariantOptionValues,
  getVariantLabel,
} from "./quick-add-drawer"
import { useCartDrawer } from "@context/cart-drawer-context"
import { usePaymentMethods } from "@context/payment-methods-context"
import { getVariantIdentifier } from "@lib/util/variants"
import Spinner from "@modules/common/icons/spinner"
import PromoBadge from "@modules/common/components/promo-badge"
import InstallmentMessage from "@modules/products/components/installment-message"
import {
  getCampaignUnavailableMessage,
  isCampaignPurchasable,
  resolveCampaignBadge,
} from "@lib/util/campaign"
import { getProductCardImageSizes } from "@lib/util/storefront-image-sizes"
import { resolveTransferUnitPricing } from "@lib/util/transfer-pricing"
import { useRouter } from "next/navigation"

export default function ProductPreview({
  product,
  isFeatured,
  region,
}: {
  product: any
  isFeatured?: boolean
  region: any
}) {
  // const pricedProduct = await listProducts({
  //   regionId: region.id,
  //   queryParams: { id: [product.id!] },
  // }).then(({ response }) => response.products[0])

  // if (!pricedProduct) {
  //   return null
  // }

  const originalPrice =
    typeof product.originalPrice === "number"
      ? product.originalPrice
      : product.price || 0
  const discountedPrice =
    typeof product.discountedPrice === "number" ? product.discountedPrice : null
  const isSale = product?.isSale === true
  const disableTransferDiscount = product?.disableTransferDiscount === true
  const hasDiscount =
    discountedPrice !== null &&
    discountedPrice > 0 &&
    discountedPrice < originalPrice
  const finalPrice = hasDiscount ? discountedPrice : originalPrice
  const computedPercent =
    hasDiscount && !isSale
      ? Math.round((1 - finalPrice / originalPrice) * 100)
      : undefined
  const displayPercent =
    !isSale && typeof product.discount === "number" && product.discount > 0
      ? product.discount
      : computedPercent
  const isPreOrder = Boolean(product?.preOrder)
  const isMadeToOrder = Boolean(product?.madeToOrder)
  const isPersonalized = Boolean(product?.personalization?.enabled)
  const campaignBadge = resolveCampaignBadge(product)
  const hasCampaignBadge = Boolean(campaignBadge) && !isSale
  const campaignPurchasable = isCampaignPurchasable(product)
  const campaignUnavailableMessage = !campaignPurchasable
    ? getCampaignUnavailableMessage(product)
    : null
  const { wire } = usePaymentMethods()
  const { showTransferPrice, transferPrice } = useMemo(
    () =>
      resolveTransferUnitPricing({
        unitPrice: finalPrice,
        disableTransferDiscount,
        settings: wire,
      }),
    [disableTransferDiscount, finalPrice, wire]
  )

  const variants: any[] = useMemo(
    () => (Array.isArray(product?.variants) ? product.variants : []),
    [product?.variants]
  )
  const defaultVariant = variants[0]

  const requiresVariantSelection = useMemo(() => {
    const optionsCount = Array.isArray(product?.options)
      ? product.options.length
      : 0
    return optionsCount > 0 && variants.length > 1
  }, [product?.options, variants.length])

  const primaryImage = useMemo(() => {
    if (
      typeof product?.thumbnail === "string" &&
      product.thumbnail.trim().length > 0
    ) {
      return product.thumbnail.trim()
    }

    if (
      typeof product?.imageUrl === "string" &&
      product.imageUrl.trim().length > 0
    ) {
      return product.imageUrl.trim()
    }

    const firstImage = product?.images?.[0]
    if (
      typeof firstImage?.url === "string" &&
      firstImage.url.trim().length > 0
    ) {
      return firstImage.url.trim()
    }

    if (
      typeof firstImage?.src === "string" &&
      firstImage.src.trim().length > 0
    ) {
      return firstImage.src.trim()
    }

    return undefined
  }, [product?.imageUrl, product?.images, product?.thumbnail])
  const posterUrl = typeof primaryImage === "string" ? primaryImage : undefined
  const productCardImageSizes = useMemo(
    () => getProductCardImageSizes(isFeatured),
    [isFeatured]
  )

  const [isAdding, setIsAdding] = useState(false)
  const [isVariantDrawerOpen, setVariantDrawerOpen] = useState(false)
  const [quickAddFeedback, setQuickAddFeedback] = useState<string | null>(null)
  const redirectTimeoutRef = useRef<number | null>(null)
  const { setCart, refreshCart } = useCart()
  const analytics = useAnalytics()
  const { open: openCartDrawer } = useCartDrawer()
  const router = useRouter()

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current !== null) {
        window.clearTimeout(redirectTimeoutRef.current)
      }
    }
  }, [])

  const getErrorMessage = useCallback((error: unknown): string => {
    if (error instanceof Error && typeof error.message === "string") {
      return error.message
    }
    if (
      error &&
      typeof error === "object" &&
      "message" in error &&
      typeof (error as { message?: unknown }).message === "string"
    ) {
      return (error as { message: string }).message
    }
    return ""
  }, [])

  const isMissingPersonalizationError = useCallback((message: string) => {
    const normalizedMessage = message.toLowerCase()
    if (!normalizedMessage.includes("personaliz")) {
      return false
    }
    return (
      normalizedMessage.includes("debes completar") ||
      normalizedMessage.includes("obligatoria") ||
      normalizedMessage.includes("inválida") ||
      normalizedMessage.includes("invalida")
    )
  }, [])

  const productRoute = resolveProductHref(product)

  const handleAddToCart = useCallback(
    async (variant?: any) => {
      if (!product?.slug) return
      setQuickAddFeedback(null)
      setIsAdding(true)
      try {
        const variantOptionValues = extractVariantOptionValues(variant)
        const variantId = getVariantIdentifier(variant)
        const metaSignals = analytics.getMetaSignals() || undefined
        const updatedCart = await addToCart({
          productSlug: product.slug,
          quantity: 1,
          variantOptionValues,
          variantId,
          metaSignals,
        })
        if (updatedCart) {
          setCart(updatedCart)
        } else {
          await refreshCart()
        }
        const addToCartEventId =
          typeof updatedCart?.eventId === "string"
            ? updatedCart.eventId
            : undefined

        const { item, currency, unitPrice } = mapProductToItem(product, {
          quantity: 1,
          variant: variant || undefined,
          fallbackCurrency: product?.currency || region?.currency_code,
        })
        analytics.trackAddToCart(
          {
            items: [item],
            currency,
            value: typeof unitPrice === "number" ? unitPrice : undefined,
          },
          {
            eventId: addToCartEventId,
          }
        )

        openCartDrawer({
          title: product?.name,
          image: product?.thumbnail,
          variantLabel: variant ? getVariantLabel(product, variant) : undefined,
          quantity: 1,
        })
      } catch (error) {
        console.error("Failed to add item to cart", error)
        const errorMessage = getErrorMessage(error)

        if (isMissingPersonalizationError(errorMessage)) {
          setQuickAddFeedback(
            "Este producto requiere personalización. Te llevamos a la ficha para completarla."
          )
          redirectTimeoutRef.current = window.setTimeout(() => {
            router.push(productRoute)
          }, 420)
        } else {
          setQuickAddFeedback(
            "No pudimos agregar el producto al carrito. Inténtalo nuevamente."
          )
        }
        await refreshCart()
      } finally {
        setIsAdding(false)
        setVariantDrawerOpen(false)
      }
    },
    [
      analytics,
      openCartDrawer,
      product,
      productRoute,
      refreshCart,
      region?.currency_code,
      router,
      setCart,
      getErrorMessage,
      isMissingPersonalizationError,
    ]
  )

  const handleQuickAdd = useCallback(() => {
    if (!campaignPurchasable) {
      return
    }
    if (requiresVariantSelection) {
      setVariantDrawerOpen(true)
      return
    }
    if (defaultVariant) {
      void handleAddToCart(defaultVariant)
    } else {
      void handleAddToCart()
    }
  }, [
    campaignPurchasable,
    defaultVariant,
    handleAddToCart,
    requiresVariantSelection,
  ])

  return (
    <div data-testid="product-wrapper" className="flex flex-col">
      <div className="relative">
        <LocalizedClientLink href={productRoute} className="group block">
          <div className="relative">
            <Thumbnail
              thumbnail={primaryImage}
              images={product.images}
              size="full"
              isFeatured={isFeatured}
              sizes={productCardImageSizes}
              videoUrl={product.videoUrl}
              posterUrl={posterUrl}
            />
            {(hasCampaignBadge ||
              isPreOrder ||
              isMadeToOrder ||
              isPersonalized ||
              isSale ||
              (hasDiscount && displayPercent)) && (
              <div className="absolute right-4 top-4 flex flex-col items-end gap-2">
                {hasCampaignBadge ? (
                  <PromoBadge className="flex min-h-[38px] min-w-[112px] max-w-[180px] items-center justify-center px-3 text-center text-[12px] font-lealti uppercase tracking-wide">
                    {campaignBadge}
                  </PromoBadge>
                ) : (
                  <>
                    {isPersonalized && (
                      <PromoBadge className="flex h-[42px] w-[120px] items-center justify-center text-[12px] font-lealti-light uppercase tracking-wide">
                        PERSONALIZADO
                      </PromoBadge>
                    )}
                    {isMadeToOrder && (
                      <PromoBadge className="flex h-[42px] w-[100px] items-center justify-center text-[12px] font-lealti-light uppercase tracking-wide">
                        A PEDIDO
                      </PromoBadge>
                    )}
                    {isPreOrder && (
                      <PromoBadge className="flex h-[42px] w-[95px] items-center justify-center text-[12px] font-lealti-light uppercase tracking-wide">
                        PRE VENTA
                      </PromoBadge>
                    )}
                    {isSale ? (
                      <PromoBadge className="flex h-[62px] w-[62px] items-center justify-center text-[12px] font-lealti-light uppercase tracking-wide">
                        SALE
                      </PromoBadge>
                    ) : hasDiscount && displayPercent ? (
                      <PromoBadge className="flex h-[62px] w-[62px] items-center justify-center text-[12px] font-lealti-light uppercase tracking-wide">
                        {displayPercent}% OFF
                      </PromoBadge>
                    ) : null}
                  </>
                )}
              </div>
            )}
          </div>
        </LocalizedClientLink>

        {!isPersonalized && (
          <button
            type="button"
            onClick={handleQuickAdd}
            disabled={isAdding || !campaignPurchasable}
            aria-label="Agregar rápidamente al carrito"
            className="absolute bottom-4 right-4 flex h-6 w-6 items-center justify-center rounded-none bg-white text-2xl font-light text-black shadow-lg transition hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isAdding ? (
              <Spinner size={12} />
            ) : (
              <span className="font-lealti-light text-md">+</span>
            )}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-1 mt-2 justify-between">
        <LocalizedClientLink
          href={productRoute}
          className="text-sm md:text-lg font-lealti-light text-black"
          data-testid="product-title"
        >
          {product.name}
        </LocalizedClientLink>

        {hasDiscount ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-lealti-light text-gray-500 line-through">
                {formatCurrency(originalPrice)}
              </span>
              {hasCampaignBadge ? (
                <PromoBadge className="flex min-h-[32px] min-w-[92px] items-center justify-center px-2 text-[12px] font-lealti uppercase tracking-wide">
                  {campaignBadge}
                </PromoBadge>
              ) : isSale ? (
                <PromoBadge className="flex h-[32px] w-[62px] items-center justify-center text-[12px] font-lealti uppercase tracking-wide">
                  SALE
                </PromoBadge>
              ) : displayPercent ? (
                <PromoBadge className="flex h-[32px] w-[62px] items-center justify-center text-[12px] font-lealti uppercase tracking-wide">
                  {displayPercent}% OFF
                </PromoBadge>
              ) : null}
              <span className="text-md md:text-lg font-lealti-light text-black">
                {formatDiscountedCurrency(finalPrice)}
              </span>
            </div>
            {showTransferPrice && (
              <div className="flex items-center gap-x-2">
                <div className="flex items-center gap-x-2 rounded-xs border border-black bg-white p-1">
                  <BiTransfer className="h-3 w-3 text-black" />
                  <span className="text-[10px] font-lealti-light tracking-[0.05em] text-black">
                    TRANSFERENCIA
                  </span>
                </div>
                <span className="text-lg font-lealti-light text-black">
                  {formatDiscountedCurrency(transferPrice)}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-row flex-wrap gap-1">
            <span className="text-lg md:text-lg font-lealti-light text-black">
              {formatCurrency(finalPrice)}
            </span>
            {showTransferPrice && (
              <div className="flex items-center gap-x-2">
                <div className="flex items-center gap-x-2 rounded-xs border border-black bg-white p-1">
                  <BiTransfer className="h-3 w-3 text-black" />
                  <span className="text-[10px] font-lealti-light tracking-[0.05em] text-black">
                    TRANSFERENCIA
                  </span>
                </div>
                <span className="text-lg font-lealti-light text-black">
                  {formatDiscountedCurrency(transferPrice)}
                </span>
              </div>
            )}
          </div>
        )}

        <InstallmentMessage
          finalPrice={finalPrice}
          currency={product.currency || region?.currency_code}
        />

        {campaignUnavailableMessage && (
          <p className="text-xs font-lealti-light text-slate-500">
            {campaignUnavailableMessage}
          </p>
        )}
        {quickAddFeedback && (
          <p className="text-xs font-lealti-light text-amber-700">
            {quickAddFeedback}
          </p>
        )}
      </div>
      <QuickAddDrawer
        product={product}
        open={isVariantDrawerOpen}
        isLoading={isAdding}
        onClose={() => setVariantDrawerOpen(false)}
        onSelectVariant={(variant) => void handleAddToCart(variant)}
      />
    </div>
  )
}
